import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  PlusIcon,
  PencilSquareIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentCheckIcon,
  LockClosedIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleSolid } from "@heroicons/react/24/solid";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { tasksApi, projectTaskTemplateSetsApi } from "../../../../api/tasks";
import { fetchChecklists } from "../../../../api/quality/checklist";
import ModernEmployeeSelector from "../../../../components/utils/Selector/ModernEmployeeSelector";
import { RECURRENCE_OPTIONS } from "../../../../pages/dashboard/todos/todoConstants";
import { OverdueRecurrenceDialog } from "../../../../pages/dashboard/todos/OverdueRecurrenceDialog";

const PRIORITY_OPTIONS = ["low", "medium", "high"];

// Convert an ISO/date value to the yyyy-mm-dd string an <input type="date"> expects.
function toDateInputValue(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// ── Shared "Promang" form styling tokens (mirrors hours/hour_form.js &
//    projects/settings/task-templates.js — keep visually consistent) ──────────
const ACCENT = {
  solid: "#1463C7",
  grad: "linear-gradient(135deg,#1463C7,#2D8BE8)",
  soft: "#EAF3FD",
  glow: "rgba(20,99,199,",
};

const labelStyle = {
  display: "block",
  fontSize: 13,
  fontWeight: 700,
  color: "#334155",
  margin: "0 0 6px",
};
const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  border: "1.5px solid #E2E8F0",
  borderRadius: 12,
  background: "#fff",
  fontSize: 14,
  fontFamily: "inherit",
  color: "#0F172A",
  outline: "none",
};
const cardStyle = {
  border: "1.5px solid #E2E8F0",
  borderRadius: 14,
  background: "#fff",
};
const primaryBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 18px",
  borderRadius: 12,
  border: "1.5px solid transparent",
  color: "#fff",
  background: ACCENT.grad,
  fontWeight: 700,
  fontSize: 14,
  fontFamily: "inherit",
  cursor: "pointer",
  boxShadow: `0 6px 14px -5px ${ACCENT.glow}.6)`,
};
const ghostBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "9px 14px",
  borderRadius: 12,
  border: "1.5px solid #E2E8F0",
  background: "#fff",
  color: "#475569",
  fontWeight: 700,
  fontSize: 13,
  fontFamily: "inherit",
  cursor: "pointer",
};
const checkboxLabelStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  fontSize: 13,
  fontWeight: 600,
  color: "#475569",
  cursor: "pointer",
};
// Small inset metadata chip used on list rows (due, reference, assignees).
const metaChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "3px 9px",
  borderRadius: 7,
  fontSize: 12,
  fontWeight: 700,
  background: "#F1F5F9",
  color: "#475569",
  fontFamily: "inherit",
};

// Status-based left accent bar / semantic tint for a task row.
const taskBarColor = (task) => {
  if (task.finished) return "#15924E";
  if (task.reference_blocked) return "#C2710C";
  return ACCENT.solid;
};

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString();
}

// Due-date buckets mirroring Task.my_day_summary (overdue / due_today / due_soon 1–7 /
// open_no_due) with an added "later" (> 7 days) bucket. Rendered as ordered sections.
const DUE_BUCKETS = ["overdue", "today", "soon", "later", "no_due"];
const BUCKET_LABEL_KEYS = {
  overdue: "project.tasks.bucket_overdue",
  today: "project.tasks.bucket_today",
  soon: "project.tasks.bucket_soon",
  later: "project.tasks.bucket_later",
  no_due: "project.tasks.bucket_no_due",
};
const BUCKET_TONE = {
  overdue: "#DC2626",
  today: ACCENT.solid,
  soon: "#0B7A3B",
  later: "#475569",
  no_due: "#94A3B8",
};

function dueBucket(task) {
  if (!task.due_date) return "no_due";
  const d = new Date(task.due_date);
  if (Number.isNaN(d.getTime())) return "no_due";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff < 0) return "overdue";
  if (diff === 0) return "today";
  if (diff <= 7) return "soon";
  return "later";
}

export const ProjectTasksTab = ({ project, canManageTasks = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const projectId = project?.id;

  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [showFinished, setShowFinished] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const finishInflight = useRef(new Set());

  const [createOpen, setCreateOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDue, setBulkDue] = useState("");
  const [bulkAssignees, setBulkAssignees] = useState([]);
  const [bulkPickerOpen, setBulkPickerOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [nextDueTask, setNextDueTask] = useState(null);
  const [nextDueBusy, setNextDueBusy] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const params = { per_page: 100 };
      if (!showFinished) params.finished = false;
      const res = await tasksApi.getProjectTasks(projectId, params);
      if (res?.data?.success) {
        setTasks(res.data.data || []);
      } else {
        setTasks([]);
      }
    } catch (error) {
      console.error("Error loading project tasks:", error);
      toast.error(t("project.tasks.load_failed"));
    } finally {
      setLoading(false);
    }
  }, [projectId, showFinished, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggleFinished = async (task) => {
    if (finishInflight.current.has(task.id)) return;
    finishInflight.current.add(task.id);
    setBusyId(task.id);
    try {
      const res = await tasksApi.toggleFinished(task.id);
      if (res?.data?.success) {
        await load();
      } else {
        const msg = res?.data?.errors?.[0];
        toast.error(msg || t("project.tasks.finish_failed"));
      }
    } catch (error) {
      const data = error?.response?.data;
      if (data?.requires_next_due) {
        setNextDueTask({ task, suggested: data.suggested_due_date });
      } else if (data?.reference_blocked) {
        toast.error(data?.errors?.[0] || t("task.referenceBlocked.checklist"));
      } else {
        toast.error(data?.errors?.[0] || t("project.tasks.finish_failed"));
      }
    } finally {
      finishInflight.current.delete(task.id);
      setBusyId(null);
    }
  };

  const toggleSelected = (taskId) => {
    setSelectedIds((current) =>
      current.includes(taskId) ? current.filter((id) => id !== taskId) : [...current, taskId]
    );
  };

  const applyBulk = async () => {
    if (!selectedIds.length) return;
    if (!bulkDue && bulkAssignees.length === 0) {
      toast.error(t("project.tasks.bulk_need_change"));
      return;
    }
    setBulkBusy(true);
    try {
      const payload = { task_ids: selectedIds };
      if (bulkDue) payload.due_date = bulkDue;
      if (bulkAssignees.length) payload.assignee_user_ids = bulkAssignees.map((row) => row.id);
      const res = await tasksApi.bulkUpdateProjectTasks(projectId, payload);
      if (res?.data?.success) {
        toast.success(t("project.tasks.bulk_done"));
        setSelectedIds([]);
        setBulkDue("");
        setBulkAssignees([]);
        await load();
      } else {
        toast.error(res?.data?.errors?.[0] || t("project.tasks.bulk_failed"));
      }
    } catch (error) {
      toast.error(error?.response?.data?.errors?.[0] || t("project.tasks.bulk_failed"));
    } finally {
      setBulkBusy(false);
    }
  };

  const openTasks = useMemo(
    () => tasks.filter((task) => !task.finished),
    [tasks]
  );
  const finishedTasks = useMemo(
    () => tasks.filter((task) => task.finished),
    [tasks]
  );

  // Group open tasks into ordered due-date buckets (client-side; no backend param needed).
  const openBuckets = useMemo(() => {
    const map = { overdue: [], today: [], soon: [], later: [], no_due: [] };
    openTasks.forEach((task) => {
      map[dueBucket(task)].push(task);
    });
    return map;
  }, [openTasks]);

  // Reset a stale date filter when its bucket becomes empty (e.g. after finishing tasks).
  useEffect(() => {
    if (dateFilter !== "all" && openBuckets[dateFilter]?.length === 0) {
      setDateFilter("all");
    }
  }, [dateFilter, openBuckets]);

  const visibleBuckets = DUE_BUCKETS.filter(
    (b) => openBuckets[b].length > 0 && (dateFilter === "all" || dateFilter === b)
  );

  if (loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <ResourceLoading />
      </div>
    );
  }

  return (
    <div
      className="space-y-4 p-4 sm:p-5"
      style={{ background: "#FFFFFF", border: "1.5px solid #E2E8F0", borderRadius: 16 }}
    >
      {/* Header */}
      <div className="flex flex-col gap-3 border-b border-[#E2E8F0] pb-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-3">
          <span
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center"
            style={{ borderRadius: 11, background: ACCENT.soft, color: ACCENT.solid }}
          >
            <ClipboardDocumentCheckIcon className="h-5 w-5" />
          </span>
          <div>
            <h2 className="text-lg font-bold text-[#0F172A]">
              {t("project.tasks.title")}
            </h2>
            <p className="text-sm text-[#64748B]">
              {t("project.tasks.subtitle")}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label style={checkboxLabelStyle} className="mr-1">
            <input
              type="checkbox"
              checked={showFinished}
              onChange={(e) => setShowFinished(e.target.checked)}
              className="h-4 w-4 rounded border-[#CBD5E1]"
            />
            {t("project.tasks.show_finished")}
          </label>
          <button
            type="button"
            onClick={() => setTemplateOpen(true)}
            style={ghostBtnStyle}
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {t("project.tasks.load_template")}
          </button>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            style={primaryBtnStyle}
          >
            <PlusIcon className="h-4 w-4" />
            {t("project.tasks.new_task")}
          </button>
        </div>
      </div>

      {canManageTasks && selectedIds.length > 0 && (
        <div className="flex flex-col gap-3 rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-3 sm:flex-row sm:items-end">
          <p className="text-sm font-semibold text-[#0F172A]">
            {t("project.tasks.selected_count", { count: selectedIds.length })}
          </p>
          <label className="text-xs font-semibold text-[#475569]">
            {t("todo.nextDueLabel")}
            <input
              type="date"
              value={bulkDue}
              onChange={(event) => setBulkDue(event.target.value)}
              className="mt-1 block min-h-[40px] rounded-xl border border-[#E2E8F0] bg-white px-3 text-sm"
            />
          </label>
          <button type="button" style={ghostBtnStyle} onClick={() => setBulkPickerOpen(true)}>
            <UserIcon className="h-4 w-4" />
            {bulkAssignees.length
              ? bulkAssignees.map((row) => row.name).join(", ")
              : t("project.tasks.add_assignees")}
          </button>
          <button type="button" style={primaryBtnStyle} disabled={bulkBusy} onClick={applyBulk}>
            {t("project.tasks.bulk_apply")}
          </button>
        </div>
      )}

      {tasks.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#CBD5E1] py-10 text-center text-sm text-[#64748B]">
          {t("project.tasks.empty")}
        </div>
      ) : (
        <div className="space-y-4">
          {openTasks.length > 0 && (
            <div
              className="flex flex-wrap gap-2"
              role="tablist"
              aria-label={t("project.tasks.filter_by_due")}
            >
              <DateFilterPill
                label={t("project.tasks.bucket_all")}
                count={openTasks.length}
                active={dateFilter === "all"}
                onClick={() => setDateFilter("all")}
              />
              {DUE_BUCKETS.filter((b) => openBuckets[b].length > 0).map((b) => (
                <DateFilterPill
                  key={b}
                  label={t(BUCKET_LABEL_KEYS[b])}
                  count={openBuckets[b].length}
                  tone={BUCKET_TONE[b]}
                  active={dateFilter === b}
                  onClick={() => setDateFilter(b)}
                />
              ))}
            </div>
          )}

          {openTasks.length === 0 ? (
            <TaskList
              tasks={[]}
              busyId={busyId}
              t={t}
              navigate={navigate}
              onToggleFinished={handleToggleFinished}
              onEdit={setEditingTask}
            />
          ) : (
            visibleBuckets.map((b) => (
              <section key={b} aria-label={t(BUCKET_LABEL_KEYS[b])}>
                <div className="mb-2 flex items-center gap-2">
                  <h3
                    className="text-xs font-bold uppercase tracking-wide"
                    style={{ color: BUCKET_TONE[b] }}
                  >
                    {t(BUCKET_LABEL_KEYS[b])}
                  </h3>
                  <span
                    style={{
                      ...metaChipStyle,
                      padding: "1px 8px",
                      fontSize: 11.5,
                    }}
                  >
                    {openBuckets[b].length}
                  </span>
                </div>
                <TaskList
                  tasks={openBuckets[b]}
                  busyId={busyId}
                  t={t}
                  navigate={navigate}
                  onToggleFinished={handleToggleFinished}
                  onEdit={setEditingTask}
                  selectedIds={selectedIds}
                  onToggleSelect={canManageTasks ? toggleSelected : undefined}
                />
              </section>
            ))
          )}

          {showFinished && finishedTasks.length > 0 && (
            <div>
              <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-[#94A3B8]">
                {t("project.tasks.finished_heading")}
              </h3>
              <TaskList
                tasks={finishedTasks}
                busyId={busyId}
                t={t}
                navigate={navigate}
                onToggleFinished={handleToggleFinished}
                onEdit={setEditingTask}
              />
            </div>
          )}
        </div>
      )}

      {createOpen && (
        <TaskFormModal
          projectId={projectId}
          t={t}
          onClose={() => setCreateOpen(false)}
          onSaved={async () => {
            setCreateOpen(false);
            await load();
          }}
        />
      )}

      {editingTask && (
        <TaskFormModal
          projectId={projectId}
          t={t}
          initialTask={editingTask}
          onClose={() => setEditingTask(null)}
          onSaved={async () => {
            setEditingTask(null);
            await load();
          }}
        />
      )}

      {templateOpen && (
        <ApplyTemplateModal
          projectId={projectId}
          t={t}
          onClose={() => setTemplateOpen(false)}
          onApplied={async () => {
            setTemplateOpen(false);
            await load();
          }}
        />
      )}

      <ModernEmployeeSelector
        open={bulkPickerOpen}
        multiple
        useDialog
        selectedEmployees={bulkAssignees}
        onClose={() => setBulkPickerOpen(false)}
        onConfirm={(selected) => {
          setBulkAssignees(selected || []);
          setBulkPickerOpen(false);
        }}
        onChange={(selected) => setBulkAssignees(selected || [])}
      />
      <OverdueRecurrenceDialog
        open={!!nextDueTask}
        suggestedDue={nextDueTask?.suggested}
        busy={nextDueBusy}
        onCancel={() => setNextDueTask(null)}
        onConfirm={async (nextDueDate) => {
          if (!nextDueTask?.task) return;
          setNextDueBusy(true);
          try {
            const res = await tasksApi.toggleFinished(nextDueTask.task.id, {
              next_due_date: nextDueDate,
            });
            if (res?.data?.success) {
              setNextDueTask(null);
              await load();
            } else {
              toast.error(res?.data?.errors?.[0] || t("project.tasks.finish_failed"));
            }
          } catch (error) {
            toast.error(error?.response?.data?.errors?.[0] || t("project.tasks.finish_failed"));
          } finally {
            setNextDueBusy(false);
          }
        }}
      />
    </div>
  );
};

// Segmented quick filter for due-date buckets — mirrors the todos workspace filter pills.
const DateFilterPill = ({ label, count, tone, active, onClick }) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className="inline-flex items-center gap-1.5"
    style={{
      padding: "7px 13px",
      borderRadius: 999,
      fontSize: 13,
      fontWeight: 700,
      fontFamily: "inherit",
      cursor: "pointer",
      border: active ? "1.5px solid transparent" : "1.5px solid #E2E8F0",
      background: active ? ACCENT.grad : "#fff",
      color: active ? "#fff" : tone || "#475569",
      boxShadow: active ? `0 6px 14px -6px ${ACCENT.glow}.55)` : "none",
    }}
  >
    <span>{label}</span>
    <span
      style={{
        display: "inline-flex",
        minWidth: 18,
        justifyContent: "center",
        padding: "0 6px",
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 700,
        background: active ? "rgba(255,255,255,0.25)" : "#F1F5F9",
        color: active ? "#fff" : "#475569",
      }}
    >
      {count}
    </span>
  </button>
);

const TaskList = ({
  tasks,
  busyId,
  t,
  navigate,
  onToggleFinished,
  onEdit,
  selectedIds = [],
  onToggleSelect,
}) => {
  if (tasks.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-[#CBD5E1] py-8 text-center text-sm text-[#64748B]">
        {t("project.tasks.no_open")}
      </div>
    );
  }

  return (
    <ul className="space-y-2.5">
      {tasks.map((task) => {
        const busy = busyId === task.id;
        const due = formatDate(task.due_date);
        const ref = task.task_reference;
        const blocked = task.reference_blocked;
        const finishBtnStyle = task.finished
          ? {
              border: "1.5px solid #BBF7D0",
              background: "#F0FDF4",
              color: "#0B7A3B",
            }
          : blocked
          ? {
              border: "1.5px solid #FBEFD9",
              background: "#fff",
              color: "#C2710C",
            }
          : {
              border: "1.5px solid #E2E8F0",
              background: "#fff",
              color: "#475569",
            };
        return (
          <li
            key={task.id}
            className="flex items-stretch overflow-hidden"
            style={cardStyle}
          >
            <span
              aria-hidden="true"
              className="flex-shrink-0"
              style={{ width: 5, background: taskBarColor(task) }}
            />
            <div className="flex flex-1 flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
              {onToggleSelect && !task.finished ? (
                <input
                  type="checkbox"
                  className="h-4 w-4 shrink-0 rounded border-[#CBD5E1]"
                  checked={selectedIds.includes(task.id)}
                  aria-label={task.title}
                  onChange={() => onToggleSelect(task.id)}
                />
              ) : null}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onEdit(task)}
                    title={t("project.tasks.edit")}
                    className={`truncate rounded text-left text-sm font-bold hover:underline focus:underline focus:outline-none ${
                      task.finished
                        ? "text-[#94A3B8] line-through"
                        : "text-[#0F172A]"
                    }`}
                    style={{ background: "transparent", cursor: "pointer" }}
                  >
                    {task.title}
                  </button>
                  {task.shared && (
                    <span
                      className="inline-flex items-center"
                      style={{
                        padding: "3px 9px",
                        borderRadius: 999,
                        fontSize: 11.5,
                        fontWeight: 700,
                        background: "#F5F3FF",
                        color: "#7C3AED",
                      }}
                    >
                      {t("project.tasks.shared")}
                    </span>
                  )}
                  {blocked && (
                    <span
                      className="inline-flex items-center gap-1"
                      style={{
                        padding: "3px 9px",
                        borderRadius: 999,
                        fontSize: 11.5,
                        fontWeight: 700,
                        background: "#FBEFD9",
                        color: "#C2710C",
                      }}
                    >
                      <LockClosedIcon className="h-3 w-3" />
                      {t("project.tasks.blocked")}
                    </span>
                  )}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {due && (
                    <span style={metaChipStyle}>
                      {t("project.tasks.due")}: {due}
                    </span>
                  )}
                  {ref && (
                    <button
                      type="button"
                      onClick={() => ref.path && navigate(ref.path)}
                      style={{ ...metaChipStyle, cursor: ref.path ? "pointer" : "default" }}
                    >
                      <ClipboardDocumentCheckIcon className="h-3 w-3" />
                      {ref.displayTitle || ref.type}
                      {ref.path && <ArrowTopRightOnSquareIcon className="h-3 w-3" />}
                    </button>
                  )}
                  {(task.assignees || []).map((a) => (
                    <span
                      key={a.id}
                      style={{ ...metaChipStyle, background: ACCENT.soft, color: ACCENT.solid }}
                    >
                      <UserIcon className="h-3 w-3" />@{a.name || a.id}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex flex-shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(task);
                  }}
                  title={t("project.tasks.edit")}
                  aria-label={t("project.tasks.edit")}
                  className="inline-flex items-center gap-1.5"
                  style={{
                    padding: "8px 14px",
                    borderRadius: 12,
                    fontSize: 13,
                    fontWeight: 700,
                    fontFamily: "inherit",
                    cursor: "pointer",
                    border: "1.5px solid #E2E8F0",
                    background: "#fff",
                    color: "#475569",
                  }}
                >
                  <PencilSquareIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">{t("project.tasks.edit")}</span>
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleFinished(task);
                  }}
                  disabled={busy}
                  title={
                    blocked
                      ? t("project.tasks.blocked_hint")
                      : task.finished
                      ? t("project.tasks.reopen")
                      : t("project.tasks.mark_finished")
                  }
                  className="inline-flex items-center gap-1.5 disabled:opacity-50"
                  style={{
                    padding: "8px 14px",
                    borderRadius: 12,
                    fontSize: 13,
                    fontWeight: 700,
                    fontFamily: "inherit",
                    cursor: busy ? "default" : "pointer",
                    ...finishBtnStyle,
                  }}
                >
                  {task.finished ? (
                    <CheckCircleSolid className="h-4 w-4" />
                  ) : (
                    <CheckCircleIcon className="h-4 w-4" />
                  )}
                  {task.finished
                    ? t("project.tasks.finished")
                    : t("project.tasks.finish")}
                </button>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

const TaskFormModal = ({ projectId, t, initialTask = null, onClose, onSaved }) => {
  const isEdit = !!initialTask;
  const [title, setTitle] = useState(initialTask?.title || "");
  const [description, setDescription] = useState(initialTask?.description || "");
  const [dueDate, setDueDate] = useState(toDateInputValue(initialTask?.due_date));
  const [priority, setPriority] = useState(initialTask?.priority || "");
  const [recurrence, setRecurrence] = useState(initialTask?.recurrence || "none");
  const [onTodayPlan, setOnTodayPlan] = useState(!!initialTask?.on_today_plan);
  const [assignees, setAssignees] = useState(
    (initialTask?.assignees || []).map((a) => ({ id: a.id, name: a.name }))
  );
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const [checklists, setChecklists] = useState([]);
  const [checklistId, setChecklistId] = useState(
    initialTask?.task_reference_type === "Checklist" && initialTask?.task_reference_id
      ? String(initialTask.task_reference_id)
      : ""
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchChecklists({ project: projectId, per_page: 100 })
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data || res?.checklists || res || [];
        setChecklists(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setChecklists([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error(t("project.tasks.title_required"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || (isEdit ? "" : undefined),
        due_date: dueDate || (isEdit ? null : undefined),
        priority: priority || (isEdit ? null : undefined),
        recurrence: recurrence || "none",
        on_today_plan: onTodayPlan,
        assignee_user_ids: assignees.map((a) => a.id),
      };
      // Always send reference keys so an edit can clear an existing checklist link.
      if (checklistId) {
        payload.task_reference_type = "Checklist";
        payload.task_reference_id = Number(checklistId);
      } else {
        payload.task_reference_type = null;
        payload.task_reference_id = null;
      }

      const res = isEdit
        ? await tasksApi.updateTask(initialTask.id, payload)
        : await tasksApi.createProjectTask(projectId, payload);

      if (res?.data?.success) {
        toast.success(
          isEdit ? t("project.tasks.updated") : t("project.tasks.created")
        );
        await onSaved();
      } else {
        toast.error(
          res?.data?.errors?.[0] ||
            (isEdit
              ? t("project.tasks.update_failed")
              : t("project.tasks.create_failed"))
        );
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.errors?.[0] ||
          (isEdit
            ? t("project.tasks.update_failed")
            : t("project.tasks.create_failed"))
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isEdit ? t("project.tasks.edit_task") : t("project.tasks.new_task")}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} style={ghostBtnStyle}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{ ...primaryBtnStyle, opacity: saving ? 0.5 : 1 }}
          >
            {isEdit ? t("project.tasks.save") : t("project.tasks.create")}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label style={labelStyle}>{t("project.tasks.field_title")}</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={inputStyle}
            autoFocus
          />
        </div>
        <div>
          <label style={labelStyle}>
            {t("project.tasks.field_description")}
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </div>
        <div className="grid grid-cols-1 items-end gap-4 sm:grid-cols-3">
          <div>
            <label style={labelStyle}>
              {t("project.tasks.field_due_date")}
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>
              {t("project.tasks.field_priority")}
            </label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              style={inputStyle}
            >
              <option value="">{t("project.tasks.priority_none")}</option>
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {t(`project.tasks.priority_${p}`)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>
              {t("project.tasks.field_recurrence")}
            </label>
            <select
              value={recurrence}
              onChange={(e) => setRecurrence(e.target.value)}
              style={inputStyle}
            >
              {RECURRENCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label style={labelStyle}>
            {t("project.tasks.field_checklist")}
          </label>
          <select
            value={checklistId}
            onChange={(e) => setChecklistId(e.target.value)}
            style={inputStyle}
          >
            <option value="">{t("project.tasks.checklist_none")}</option>
            {checklists.map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayTitle || c.title || `#${c.id}`}
              </option>
            ))}
          </select>
          {checklistId && (
            <p className="mt-1.5 text-xs font-semibold text-[#C2710C]">
              {t("project.tasks.checklist_gate_hint")}
            </p>
          )}
        </div>
        <div>
          <label style={labelStyle}>
            {t("project.tasks.field_assignees")}
          </label>
          <div className="flex flex-wrap items-center gap-2">
            {assignees.map((a) => (
              <span
                key={a.id}
                style={{ ...metaChipStyle, background: ACCENT.soft, color: ACCENT.solid }}
              >
                @{a.name || a.id}
              </span>
            ))}
            <button
              type="button"
              onClick={() => setAssigneeOpen(true)}
              style={ghostBtnStyle}
            >
              <PlusIcon className="h-4 w-4" />
              {t("project.tasks.add_assignees")}
            </button>
          </div>
        </div>
        <label style={checkboxLabelStyle}>
          <input
            type="checkbox"
            checked={onTodayPlan}
            onChange={(e) => setOnTodayPlan(e.target.checked)}
            className="h-4 w-4 rounded border-[#CBD5E1]"
          />
          {t("project.tasks.on_today_plan")}
        </label>
      </div>

      <ModernEmployeeSelector
        open={assigneeOpen}
        multiple
        useDialog
        selectedEmployees={assignees}
        onClose={() => setAssigneeOpen(false)}
        onConfirm={(selected) => {
          setAssignees(selected || []);
          setAssigneeOpen(false);
        }}
        onChange={(selected) => setAssignees(selected || [])}
      />
    </Modal>
  );
};

const ApplyTemplateModal = ({ projectId, t, onClose, onApplied }) => {
  const [loading, setLoading] = useState(true);
  const [sets, setSets] = useState([]);
  const [applyingId, setApplyingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    projectTaskTemplateSetsApi
      .list({ active: true })
      .then((res) => {
        if (cancelled) return;
        setSets(res?.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setSets([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleApply = async (set) => {
    setApplyingId(set.id);
    try {
      const res = await tasksApi.applyProjectTaskTemplateSet(projectId, set.id);
      if (res?.data?.success) {
        const count = res?.data?.meta?.created_tasks ?? 0;
        toast.success(t("project.tasks.template_applied", { count }));
        await onApplied();
      } else {
        toast.error(
          res?.data?.errors?.[0] || t("project.tasks.template_failed")
        );
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.errors?.[0] || t("project.tasks.template_failed")
      );
    } finally {
      setApplyingId(null);
    }
  };

  return (
    <Modal title={t("project.tasks.load_template")} onClose={onClose}>
      {loading ? (
        <div className="flex min-h-[160px] items-center justify-center">
          <ResourceLoading />
        </div>
      ) : sets.length === 0 ? (
        <p className="py-6 text-center text-sm text-[#64748B]">
          {t("project.tasks.no_templates")}
        </p>
      ) : (
        <ul className="space-y-2.5">
          {sets.map((set) => (
            <li
              key={set.id}
              className="flex items-center justify-between gap-3 p-3"
              style={cardStyle}
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-[#0F172A]">
                  {set.title}
                </p>
                <p className="truncate text-xs text-[#64748B]">
                  {[set.category, t("project.tasks.template_item_count", { count: set.item_count ?? 0 })]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleApply(set)}
                disabled={applyingId === set.id}
                style={{
                  ...primaryBtnStyle,
                  padding: "8px 14px",
                  fontSize: 13,
                  opacity: applyingId === set.id ? 0.5 : 1,
                }}
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                {t("project.tasks.apply")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
};

const Modal = ({ title, children, footer, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0F172A]/40 p-4">
    <div
      className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden bg-white shadow-2xl"
      style={{ borderRadius: 18 }}
    >
      <div className="flex items-center justify-between border-b border-[#E2E8F0] px-5 py-4">
        <h3 className="truncate text-base font-bold text-[#0F172A]">
          {title}
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-[#94A3B8] hover:bg-[#F1F5F9] hover:text-[#475569]"
          aria-label="Close"
        >
          ✕
        </button>
      </div>
      <div className="flex-1 overflow-y-auto bg-[#F8FAFC] p-5">{children}</div>
      {footer && (
        <div className="border-t border-[#E2E8F0] px-5 py-4">{footer}</div>
      )}
    </div>
  </div>
);

export default ProjectTasksTab;
